/**
 * KAN-862: полоса вкладок колонок мобильной доски (название и счётчик);
 * своя горизонтальная прокрутка, активная вкладка видна.
 * KAN-875: в браузере компьютера строка прокручивается колесом, перетаскиванием
 * мышью и стрелками (useHorizontalScroll).
 */
import { useEffect, useRef } from 'react';
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll';
import { cn } from '@/lib/utils';
import { ScrollArrows } from './ScrollArrows';
import type { CrmBoardColumn } from '@/types/crmPipeline';

interface KanbanColumnTabsProps {
  columns: readonly CrmBoardColumn[];
  active: string | null;
  onSelect: (key: string) => void;
}

export function KanbanColumnTabs({ columns, active, onSelect }: KanbanColumnTabsProps): JSX.Element {
  const listRef = useRef<HTMLDivElement | null>(null);
  const scroll = useHorizontalScroll<HTMLDivElement>({ externalRef: listRef });

  useEffect(() => {
    const tab = listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (tab && typeof tab.scrollIntoView === 'function') {
      tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [active]);

  return (
    <div className="relative -mx-4 mb-2">
      <div
        ref={scroll.ref}
        role="tablist"
        aria-label="Колонки"
        data-testid="kanban-column-tabs"
        className="flex gap-1 overflow-x-auto px-4 pb-1 [scrollbar-width:none]"
      >
      {columns.map((column) => {
        const selected = column.key === active;
        return (
          <button
            key={column.key}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onSelect(column.key)}
            className={cn(
              'inline-flex min-h-[36px] shrink-0 items-center gap-1.5 rounded-full px-3 text-sm font-medium',
              selected ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-200'
            )}
          >
            <span className="max-w-[10rem] truncate">{column.title}</span>
            <span className={cn('text-xs', selected ? 'text-blue-100' : 'text-gray-400')}>{column.count}</span>
          </button>
        );
      })}
      </div>
      <ScrollArrows
        canScrollLeft={scroll.canScrollLeft}
        canScrollRight={scroll.canScrollRight}
        onScroll={scroll.scrollPage}
      />
    </div>
  );
}
